import { c as _c } from "react/compiler-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/DropdownMenu';
import { ChevronDown } from '@/components/ui/icons/Icons';
import { useI18n } from '@/context/I18nProvider';
import { useManualReplyPreference } from '@/hooks/useManualReplyPreference';
import { cn } from '@/lib/utils';
import { ApiClient } from '@/services/api-client';
import { GetLeadByIDResponse } from '@/services/types/global.types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
type Mode = 'org' | 'keep' | 'stop';
const toMode = (override: boolean | null | undefined): Mode => override === true ? 'keep' : override === false ? 'stop' : 'org';
const toOverride = (mode: Mode): boolean | null => mode === 'keep' ? true : mode === 'stop' ? false : null;
type ManualReplyModeMenuProps = {
  leadId: string;
  /** The conversation's override; null/undefined follows the workspace setting. */
  keepAgentOnManualReply?: boolean | null;
};

/**
 * Per-conversation choice of what a manual reply does to the AI agent: follow
 * the workspace setting, always keep the agent on, or always switch it off.
 */
const ManualReplyModeMenu = t0 => {
  const $ = _c(66);
  const {
    leadId,
    keepAgentOnManualReply
  } = t0;
  const {
    t
  } = useI18n();
  const queryClient = useQueryClient();
  const {
    data: t1
  } = useManualReplyPreference();
  const orgKeepsAgentOn = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== keepAgentOnManualReply) {
    t2 = toMode(keepAgentOnManualReply);
    $[0] = keepAgentOnManualReply;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const mode = t2;
  let t3;
  if ($[2] !== leadId) {
    t3 = next => ApiClient.lead.updateConversationManualReply({
      leadId,
      keepAgentOnManualReply: toOverride(next)
    });
    $[2] = leadId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== leadId || $[5] !== queryClient || $[6] !== t) {
    t4 = (_res, next_0) => {
      queryClient.setQueryData(["lead.getLead", {
        id: leadId
      }], old => {
        if (!old?.data) {
          return old;
        }
        return {
          ...old,
          data: {
            ...old.data,
            KeepAgentOnManualReply: toOverride(next_0)
          }
        };
      });
      toast.success(t("conversations.manualReplyMode.saved"));
    };
    $[4] = leadId;
    $[5] = queryClient;
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = () => toast.error(t("conversations.manualReplyMode.error"));
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = {
      mutationFn: t3,
      onSuccess: t4,
      onError: t5
    };
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const mUpdate = useMutation(t6);
  let t7;
  if ($[14] !== t) {
    t7 = t("conversations.manualReplyMode.trigger");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("conversations.manualReplyMode.trigger");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = mode !== "org" && "text-blue-500 hover:text-blue-600";
  let t10;
  if ($[18] !== t9) {
    t10 = cn("flex size-4 items-center justify-center rounded text-muted-foreground hover:text-foreground", t9);
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ChevronDown className="size-3.5" />;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== mUpdate.isPending || $[22] !== t10 || $[23] !== t7 || $[24] !== t8) {
    t12 = <DropdownMenuTrigger asChild={true}><button type="button" aria-label={t7} title={t8} className={t10} disabled={mUpdate.isPending}>{t11}</button></DropdownMenuTrigger>;
    $[21] = mUpdate.isPending;
    $[22] = t10;
    $[23] = t7;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("conversations.manualReplyMode.label");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13) {
    t14 = <p className="text-sm font-medium text-text-primary">{t13}</p>;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t) {
    t15 = t("conversations.manualReplyMode.hint");
    $[30] = t;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t15) {
    t16 = <p className="text-xs font-normal text-muted-foreground">{t15}</p>;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t14 || $[35] !== t16) {
    t17 = <DropdownMenuLabel className="space-y-0.5">{t14}{t16}</DropdownMenuLabel>;
    $[34] = t14;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <DropdownMenuSeparator />;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== mUpdate || $[39] !== mode) {
    t19 = value => {
      if (value !== mode) {
        mUpdate.mutate(value as Mode);
      }
    };
    $[38] = mUpdate;
    $[39] = mode;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== orgKeepsAgentOn || $[42] !== t) {
    t20 = t("conversations.manualReplyMode.org", {
      mode: orgKeepsAgentOn ? t("conversations.manualReplyMode.orgKeep") : t("conversations.manualReplyMode.orgStop")
    });
    $[41] = orgKeepsAgentOn;
    $[42] = t;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t20) {
    t21 = <DropdownMenuRadioItem value="org" className="text-sm">{t20}</DropdownMenuRadioItem>;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t) {
    t22 = t("conversations.manualReplyMode.keep");
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t22) {
    t23 = <DropdownMenuRadioItem value="keep" className="text-sm">{t22}</DropdownMenuRadioItem>;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t) {
    t24 = t("conversations.manualReplyMode.stop");
    $[50] = t;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t24) {
    t25 = <DropdownMenuRadioItem value="stop" className="text-sm">{t24}</DropdownMenuRadioItem>;
    $[52] = t24;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== mode || $[55] !== t19 || $[56] !== t21 || $[57] !== t23 || $[58] !== t25) {
    t26 = <DropdownMenuRadioGroup value={mode} onValueChange={t19}>{t21}{t23}{t25}</DropdownMenuRadioGroup>;
    $[54] = mode;
    $[55] = t19;
    $[56] = t21;
    $[57] = t23;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t17 || $[61] !== t26) {
    t27 = <DropdownMenuContent align="end" className="w-72 rounded-lg border-gray-100">{t17}{t18}{t26}</DropdownMenuContent>;
    $[60] = t17;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t12 || $[64] !== t27) {
    t28 = <DropdownMenu>{t12}{t27}</DropdownMenu>;
    $[63] = t12;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  return t28;
};
export { ManualReplyModeMenu };